import type { ReactNode } from "react";
import { actionProps, scopeProps } from "@/lib/controlAnalytics";
import { PREVIEW_COPY, type PreviewKind } from "@/lib/previews";
import { useGate } from "../gateContext";
import PlaceLink from "../PlaceLink";
import { GRAD_BUTTON } from "../styles";

interface PlacePreviewProps {
    kind: PreviewKind;
    /** A plain link beside the action, when the place has one. */
    secondary?: { label: string; to: string };
    /** A short line under the action. */
    note?: ReactNode;
    children: ReactNode;
}

interface AskProps extends Omit<PlacePreviewProps, "children"> {
    label: string;
    /** Set on the ask under the example, so the analytics tell the two apart. */
    step?: "closing";
    className?: string;
}

/** The place's one action, with its link and note. */
function Ask({ kind, secondary, note, label, step, className = "items-start" }: AskProps) {
    const { ask } = useGate();
    const { gate } = PREVIEW_COPY[kind];
    return (
        <div className={`flex flex-col gap-2 ${className}`}>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <button
                    {...actionProps("join_signup_clicked", {
                        source: "PlacePreview",
                        section: kind,
                        step,
                    })}
                    type="button"
                    onClick={() => ask(gate)}
                    className={`${GRAD_BUTTON} h-[46px] rounded-[12px] px-5 text-[14px]`}
                >
                    {label}
                </button>
                {secondary && (
                    <PlaceLink
                        {...actionProps("navigation_clicked", {
                            source: "PlacePreview",
                            section: kind,
                            target: secondary.to,
                            step,
                        })}
                        to={secondary.to}
                        className="text-[14px] font-semibold text-brand-ink hover:underline"
                    >
                        {secondary.label}
                    </PlaceLink>
                )}
            </div>
            {note && <p className="m-0 text-[13px] text-fg-muted">{note}</p>}
        </div>
    );
}

/**
 * A place an account keeps, for a visitor: one short introduction, the
 * place itself filled with the example (lib/example.ts), labelled as an
 * example, and one action. A place with a closing line asks again under
 * the example, in those words. The account is asked for only when the
 * visitor wants the place with their own data. The words are
 * lib/previews.ts's.
 */
export default function PlacePreview({ kind, secondary, note, children }: PlacePreviewProps) {
    const { place, title, line, action, closing, exampleOf } = PREVIEW_COPY[kind];
    return (
        <section
            {...scopeProps({ source: "PlacePreview", section: kind })}
            aria-labelledby="preview-title"
            className="flex flex-col gap-6 pb-4"
        >
            <header className="flex flex-wrap items-end justify-between gap-x-8 gap-y-4">
                <div className="flex max-w-[620px] flex-col gap-2">
                    <span className="text-[13px] font-semibold text-brand-ink">{place}</span>
                    <h1
                        id="preview-title"
                        className="m-0 font-display text-[40px] font-extrabold leading-[1.06] tracking-[-0.03em] [text-wrap:balance] max-[760px]:text-[30px]"
                    >
                        {title}
                    </h1>
                    <p className="m-0 text-[16px] leading-normal text-fg-muted [text-wrap:pretty]">
                        {line}
                    </p>
                </div>
                <Ask kind={kind} secondary={secondary} note={note} label={action} />
            </header>
            <div className="flex flex-col gap-3 rounded-[20px] border border-dashed border-line-strong bg-card-raised p-3 max-[560px]:p-2">
                <p className="m-0 flex flex-wrap items-center gap-2 px-1.5 pt-0.5 text-[12.5px] text-fg-muted">
                    <span className="rounded-full bg-card px-2.5 py-[3px] text-[11px] font-bold uppercase tracking-[0.06em] text-fg">
                        Example
                    </span>
                    {exampleOf} Jordan Lee is made up. Yours shows here once you have an account.
                </p>
                {children}
            </div>
            {closing && (
                <Ask
                    kind={kind}
                    secondary={secondary}
                    note={note}
                    label={closing}
                    step="closing"
                    className="items-center self-center pt-2"
                />
            )}
        </section>
    );
}
